<script setup lang="ts">
// Note: if this is being rendered in a surface component, you should render portal its parent to SurfacePanels.
// ie <Portal selector="#surface-panels"> <ContentPicker /> </Portal>
import { trackEvent } from '@@/bits/analytics'
import { isAppCapableOf } from '@@/bits/app_can'
import { supportsRecording, supportsVideoRecording } from '@@/bits/av_recorder'
import { STOP_HAND_ICON } from '@@/bits/confirmation_dialog'
import type { ContentPickerSourceMenuListItem } from '@@/bits/content_picker'
import { ContentPickerSource } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { loginWithReferrerUrl, navigateTo } from '@@/bits/location'
import { FILE_TOO_LARGE_ERROR, UploadJob } from '@@/bits/uploader'
import { defineAsyncComponent } from '@@/bits/vue'
import { checkForPadletMiniChromeExtension } from '@@/bits/web_extensions'
import type { SearchFilters } from '@@/bits/web_search'
import type { AppUploadAvatarIntent } from '@@/native_bridge/actions'
import { browserFile, openCamera } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import type { ContentPickerSearchMode } from '@@/pinia/content_picker'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { PadletSelectionIntent } from '@@/pinia/padlet_picker'
import { useScreenRecorderStore } from '@@/pinia/screen_recorder_store'
// renamed screen_recorder_store to surface_screen_recorder_store to reflect that it handles screen recording in surface only, should not be breaking since only renaming
// disabling the ww.no_surface_or_dash_store_in_content_picker rule for if/when we refactor this store to be agnostic
import { useSurfaceScreenRecorderStore } from '@@/pinia/surface_screen_recorder_store' // goodcheck-disable-line
import type { DraftPost, User, Wall, WallPostProperties } from '@@/types'
import ContentPickerSourceMenu from '@@/vuecomponents/ContentPickerSourceMenu.vue'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const PhotoBoothV2 = defineAsyncComponent(() => import('@@/vuecomponents/PhotoBoothV2.vue'))
const SurfaceVideoRecorder = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceVideoRecorder.vue'))
const VideoRecorder = defineAsyncComponent(() => import('@@/vuecomponents/VideoRecorder.vue'))
const AudioRecorderV2 = defineAsyncComponent(() => import('@@/vuecomponents/AudioRecorderV2.vue'))
const SurfacePadletPickerV2 = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePadletPickerV2.vue'))
const PadletPicker = defineAsyncComponent(() => import('@@/vuecomponents/PadletPicker.vue'))
const DrawingCanvas = defineAsyncComponent(() => import('@@/vuecomponents/DrawingCanvas.vue'))
const MapBrowser = defineAsyncComponent(() => import('@@/vuecomponents/map_browser.vue'))
const SurfaceLocationPickerV2 = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceLocationPickerV2.vue'))
const LocationPicker = defineAsyncComponent(() => import('@@/vuecomponents/LocationPicker.vue'))
const ScreenshareRecorder = defineAsyncComponent(() => import('@@/vuecomponents/screenshare_recorder.vue'))
const SurfacePollBuilder = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePollBuilder.vue'))
const GoogleDrivePicker = defineAsyncComponent(() => import('@@/vuecomponents/GoogleDrivePicker.vue'))
const AiTalk = defineAsyncComponent(() => import('@@/vuecomponents/AiTalk.vue'))
const StickerPickerModal = defineAsyncComponent(() => import('@@/vuecomponents/StickerPickerModal.vue'))

const props = withDefaults(
  defineProps<{
    isOnline: boolean
    contentSourceMenuList: Array<Array<ContentPickerSourceMenuListItem>>
    preferredContentSourceMenuStartHeight: number
    enableHiddenInputs: boolean
    /**
     * Whether or not the content picker should use agnostic content panels.
     * This is a temporary prop to allow transitioning surface-store-reliant panels to agnostic panels while
     * maintaining current behavior (add post link requires agnostic panels)
     */
    useAgnosticContentPanels?: boolean
    searchSuggestions?: string[]
    searchModes?: ContentPickerSearchMode[]
    defaultSearchMode?: SearchFilters
    /**
     * Specify the intention of content picker in mobile app (e.g. upload user avatar, etc).
     * This is necessary because mobile app uses native content picker.
     */
    appIntent?: AppUploadAvatarIntent
    /**
     * Whether or not the search bar can accept a link (addding a link directly from the search bar).
     * If this is `false`, the search bar will always search the input text.
     */
    canAcceptLinkInSearchBar?: boolean
    /**
     * Specific to the Google Drive picker, this differentiates the uploads from each other (posts, comments, etc).
     */
    googleDriveUploadKey?: string
    /**
     * Specific to the Google Drive picker, this specifies the file types that the picker should show.
     */
    googleDrivePickerFileTypes?: 'all' | 'images' | 'images_videos'
    googleDriveApiKey?: string
    googleAppId?: string
    maxFileUploadSize?: number
    /**
     * Whether or not the content picker should do the uploading of the file.
     */
    shouldUploadFile?: boolean
    activeDraft?: DraftPost | null
    xSearchBar?: boolean
    user?: User
    wall?: Wall
    wallPostProperties?: WallPostProperties
    contentPickerContext?: string
    isLtiEmbedded?: boolean
    multipleImagesSupported?: boolean
    acceptOnlyImages?: boolean
    maxFiles?: number
  }>(),
  {
    useAgnosticContentPanels: false,
    searchSuggestions: undefined,
    searchModes: undefined,
    defaultSearchMode: undefined,
    appIntent: undefined,
    canAcceptLinkInSearchBar: true,
    googleDriveUploadKey: undefined,
    googleDrivePickerFileTypes: 'all',
    googleDriveApiKey: undefined,
    googleAppId: undefined,
    maxFileUploadSize: MAX_FILE_UPLOAD_SIZE,
    shouldUploadFile: true,
    activeDraft: undefined,
    xSearchBar: true,
    user: undefined,
    wall: undefined,
    wallPostProperties: undefined,
    contentPickerContext: undefined,
    isLtiEmbedded: false,
    multipleImagesSupported: false,
    acceptOnlyImages: false,
    maxFiles: undefined,
  },
)

/**
 * NOTE: When a content is picked from the content picker, we should emit
 * the `contentSource` as well. This is useful for WhiteboardAttachmentPicker
 * to know the source of the content to filter it based on __allowed tools__.
 * The `select-file`, `select-link` & `update-active-draft` events already
 * do this and it's enough for whiteboard but if we want to do the filtering
 * based on other events, we should ad the `contentSource` to those events'
 * payload as well.
 */
const emit = defineEmits<{
  (name: 'close-content-picker'): void
  (name: 'open-in-new-window'): void
  (name: 'upload-start', payload: { file?: File; url?: string }): void
  (name: 'upload-done', url: string): void
  (name: 'upload-cancel', file: File): void
  (name: 'upload-reject', file: File): void
  (name: 'upload-error', file?: File): void
  (name: 'upload-progress', progress: number): void
  (
    name: 'select-file',
    payload: {
      file: File
      contentSource: ContentPickerSource
      trimVideoAfterUpload?: { start: number; end: number }
    },
  ): void
  (name: 'select-link', link: string, payload: { altText?: string; contentSource: ContentPickerSource }): void
  (name: 'set-caption', caption: string): void
  (name: 'show-extension-required'): void
  (
    name: 'update-active-draft',
    wishContent: {
      wish_content: { attachment_props: any }
    },
    contentSource: ContentPickerSource,
  ): void
  (name: 'max-files-exceeded'): void
}>()

const isChrome = device.chrome
const isFirefox = device.firefox
const isMobile = device.mobile

const contentPickerStore = useContentPickerStore()
const { zIndexOverride } = storeToRefs(contentPickerStore)
const { activeContentPickerPanel, xContentSourceMenu, isHiddenContentSourceMenu, contentPickerContext } =
  storeToRefs(contentPickerStore)
const { hideContentPickerPanel, hideContentSourceMenu, pickContentSource } = contentPickerStore

const isContentPickerContextMatch = computed(
  // Ignore contentPickerContext if props is not provided
  () => props.contentPickerContext == null || contentPickerContext.value === props.contentPickerContext,
)
const chromeExtensionInstalled = ref<boolean | undefined>(undefined)
const uploadJob = ref<UploadJob | null>(null)

const activePanel = computed<ContentPickerSource | null>(() => activeContentPickerPanel.value)

const screenshareEnabledByDefault = computed<boolean>(() => {
  const screenshareApiEnabled = (isChrome && !!window.chrome && !!window.chrome.desktopCapture) || isFirefox
  return supportsRecording && !isMobile && screenshareApiEnabled
})

const screenshareSupport = computed<boolean>(() => supportsVideoRecording && !isMobile && (isChrome || isFirefox))

const canAppUseNativeCameraAndFilePicker = computed<boolean>(
  () => device.app && isAppCapableOf('nativeDashboardSettingsContentPicker'),
)

const pollDraft = computed(() => {
  return props.activeDraft?.wish_content?.attachment_props?.type === 'poll'
    ? (props.activeDraft?.wish_content?.attachment_props as any)
    : null
})

const audioLengthLimit = computed<number | undefined>(() => {
  if (!isAppUsing('mediaRecorderQuotas')) return undefined
  return props.wall?.audio_length_limit
})

const videoLengthLimit = computed<number | undefined>(() => {
  if (!isAppUsing('mediaRecorderQuotas')) return undefined
  if (props.wallPostProperties?.video_length_limit) return props.wallPostProperties.video_length_limit // based on wall settings
  return props.wall?.video_length_limit // based on wall owner's plan
})

const uploadFile = (file: File): void => {
  uploadJob.value = new UploadJob(file, { maxFileSize: props.maxFileUploadSize })
  uploadJob.value.perform()

  uploadJob.value.on('done', (response) => onUploadDone(response.url as string))
  uploadJob.value.on('cancel', () => onUploadCancel(file))
  uploadJob.value.on('error', (response) => {
    if (response.error === FILE_TOO_LARGE_ERROR) {
      onUploadReject(file)
    } else {
      onUploadError(file)
    }
  })
}

const onUploadDone = (url: string): void => {
  uploadJob.value = null
  emit('upload-done', url)
  closePanel()
}

const onUploadCancel = (file: File): void => {
  emit('upload-cancel', file)
  closePanel()
}

const onUploadReject = (file: File): void => {
  emit('upload-reject', file)
  closePanel()
}

const onUploadError = (file: File): void => {
  emit('upload-error', file)
  closePanel()
}

// #region Validate max files
const isMaxFilesExceeded = (e: Event): boolean => {
  const input = e.target as HTMLInputElement
  if (props.maxFiles && input.files && input.files.length > props.maxFiles) {
    emit('max-files-exceeded')
    return true
  }
  return false
}
// #endregion
const selectFile = (e, { contentSource }: { contentSource: ContentPickerSource }): void => {
  if (isMaxFilesExceeded(e)) return

  if (props.multipleImagesSupported && e.target.files && e.target.files.length > 0) {
    const files = Array.from(e.target.files) as File[]
    files.forEach((file) => {
      if (!props.shouldUploadFile) {
        emit('select-file', { file, contentSource })
      } else {
        emit('upload-start', { file })
        uploadFile(file)
      }
    })
  } else {
    if (!props.shouldUploadFile) {
      emit('select-file', { file: e.target.files[0], contentSource })
    } else {
      emit('upload-start', { file: e.target.files[0] })
      uploadFile(e.target.files[0])
    }
  }
  closeSourceMenu()
}

const selectLink = (link: string, payload: { altText?: string; contentSource: ContentPickerSource }): void => {
  emit('select-link', link, payload)
  closePanel()
}

const selectWall = (wall: Wall): void => {
  selectLink(wall.links.permanent ?? wall.links.show, { contentSource: ContentPickerSource.PadletBrowser })
}

const startFileUpload = (
  file: File,
  {
    contentSource,
    trimVideoAfterUpload,
  }: { contentSource: ContentPickerSource; trimVideoAfterUpload?: { start: number; end: number } },
): void => {
  if (!props.shouldUploadFile) {
    emit('select-file', { file, contentSource, trimVideoAfterUpload })
  } else {
    emit('upload-start', { file })
    uploadFile(file)
  }
  closeSourceMenu()
}

const setCaption = (caption: string): void => {
  if (!caption) {
    return
  }

  emit('set-caption', caption)
}

const closeSourceMenu = (): void => {
  hideContentSourceMenu()
  // If we close source menu without selecting a source, close the whole content picker.
  if (activeContentPickerPanel.value == null) {
    emit('close-content-picker')
  }
}

const closePanel = (): void => {
  hideContentPickerPanel()
  // If we close a content picker panel and the source menu is also closed, close the whole content picker.
  if (!xContentSourceMenu.value) {
    emit('close-content-picker')
  }
}

const surfaceScreenRecorderStore = useSurfaceScreenRecorderStore()
const screenRecorderStore = useScreenRecorderStore()
const activatePanel = (source: ContentPickerSource): void => {
  // handle native camera and file picker
  if (canAppUseNativeCameraAndFilePicker.value && source === ContentPickerSource.FileBrowser && props.appIntent) {
    postMessage(browserFile(props.appIntent))
    return
  } else if (canAppUseNativeCameraAndFilePicker.value && source === ContentPickerSource.PhotoBooth && props.appIntent) {
    postMessage(openCamera(props.appIntent))
    return
  }

  if (
    !isAppUsing('screenRecorderV2') &&
    source === ContentPickerSource.ScreenshareRecorder &&
    screenshareSupport.value &&
    !screenshareEnabledByDefault.value
  ) {
    if (chromeExtensionInstalled.value === true) {
      pickContentSource({ source })
    } else {
      emit('show-extension-required')
    }
  } else if (source === ContentPickerSource.VideoRecorder) {
    pickContentSource({ source })
    return
  } else if (
    ((source === ContentPickerSource.FileBrowser || source === ContentPickerSource.PhotoBooth) && isMobile) ||
    source === ContentPickerSource.FileBrowser
  ) {
    // Do nothing because the hidden input will be activated based on
    // the ContentSourceMenuListItem properties.
    return
  } else if (source === ContentPickerSource.ScreenshareRecorder && isAppUsing('screenRecorderV2')) {
    if (props.useAgnosticContentPanels) {
      screenRecorderStore.showScreenRecorder()
    } else {
      surfaceScreenRecorderStore.saveCopyOfCurrentDraft()
      surfaceScreenRecorderStore.showScreenRecorder()
    }
    closeSourceMenu()
    return
  }

  pickContentSource({ source })
}

// TODO: move this outside of ContentPicker so this component isn't coupled
// with posts/drafts
const createPoll = (pollData) => {
  const wishContent = {
    attachment_props: { ...pollData, type: 'poll' },
  }

  emit('update-active-draft', { wish_content: wishContent }, ContentPickerSource.Poll)
  closeSourceMenu()
}

const editPoll = (pollData) => {
  const wishContent = {
    attachment_props: { ...pollData, edit_poll: true },
  }
  emit('update-active-draft', { wish_content: wishContent }, ContentPickerSource.Poll)
  closeSourceMenu()
}

// TODO: move this outside of ContentPicker so this component isn't coupled
// with posts/drafts
const saveAiTalkAudio = (data: {
  attachment: string
  attachment_caption: string
  wish_content: { attachment_props: { type: string; url: string; text: string } }
}) => {
  emit('update-active-draft', data, ContentPickerSource.AiTalk)
  closeSourceMenu()
}

function checkAndCloseContentPicker(): void {
  if (contentPickerStore.isHiddenContentSourceMenu) {
    closeSourceMenu()
  }
}

const hiddenInputCapture = computed<'environment' | undefined>(() => {
  if (contentPickerStore.contentSourceToManuallyTrigger === ContentPickerSource.PhotoBooth) return 'environment'
  if (contentPickerStore.contentSourceToManuallyTrigger === ContentPickerSource.VideoRecorder) return 'environment'
  return undefined
})

onMounted((): void => {
  checkForPadletMiniChromeExtension().then((isInstalled) => {
    chromeExtensionInstalled.value = isInstalled
  })
  nextTick(() => {
    if (
      contentPickerStore.hiddenInputToManuallyTrigger != null &&
      contentPickerStore.contentSourceToManuallyTrigger != null
    ) {
      document.getElementById(contentPickerStore.hiddenInputToManuallyTrigger)?.click()
      if (contentPickerStore.contentSourceToManuallyTrigger === ContentPickerSource.FileBrowser) {
        activatePanel(contentPickerStore.contentSourceToManuallyTrigger)
      }
    }
  })
})

onBeforeUnmount((): void => {
  if (uploadJob.value) {
    uploadJob.value.cancel()
  }
})

watch(
  () => contentPickerStore.hiddenInputToManuallyTrigger,
  (hiddenInputId) => {
    if (hiddenInputId != null && contentPickerStore.contentSourceToManuallyTrigger != null) {
      document.getElementById(hiddenInputId)?.click()
      if (contentPickerStore.contentSourceToManuallyTrigger === ContentPickerSource.FileBrowser) {
        activatePanel(contentPickerStore.contentSourceToManuallyTrigger)
      }
    }
  },
)

// # region Padlet Picker is only shown to registered users
const isUserRegistered = computed(() => props.user?.registered_at != null)
if (isAppUsing('padletPickerV2')) {
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()
  const isPadletAttachmentTypeSelected = computed(() => activePanel.value === ContentPickerSource.PadletBrowser)
  watch(
    isPadletAttachmentTypeSelected,
    (value) => {
      if (!value) return
      if (!isUserRegistered.value) {
        trackEvent('Padlet Picker V2', 'Prompted to log in to add padlet attachment')
        globalConfirmationDialogStore.openConfirmationDialog({
          ...STOP_HAND_ICON,
          title: __('Must be logged in to add padlet attachments'),
          body: __('Please log in or sign up to add a padlet as an attachment'),
          confirmButtonText: __('Log in or sign up'),
          cancelButtonText: __('Nevermind'),
          forceFullWidthButtons: true,
          afterConfirmActions: [
            () => {
              if (!props.wall) return
              navigateTo(loginWithReferrerUrl(props.wall.links.permanent))
            },
          ],
          afterCancelActions: [closePanel],
        })
      }
    },
    {
      immediate: true,
    },
  )
}
// # endregion Padlet picker

const contentPickerContainerStyle = computed(() =>
  zIndexOverride.value != null ? { zIndex: zIndexOverride.value } : undefined,
)

defineExpose({
  activatePanel,
})
</script>

<script lang="ts">
const MAX_FILE_UPLOAD_SIZE = 31457280
export default {}
</script>

<template>
  <div id="content-picker" class="fixed top-0 z-modal2 start-0" :style="contentPickerContainerStyle">
    <ContentPickerSourceMenu
      v-if="xContentSourceMenu"
      v-show="!isHiddenContentSourceMenu && isContentPickerContextMatch"
      :is-online="isOnline"
      :menu-list="contentSourceMenuList"
      :search-modes="searchModes"
      :default-search-mode="defaultSearchMode"
      :can-accept-link-in-search-bar="canAcceptLinkInSearchBar"
      :search-suggestions="searchSuggestions"
      :preferred-tray-start-height="500"
      :x-search-bar="xSearchBar"
      @close="closeSourceMenu"
      @select="activatePanel"
      @select-link="selectLink"
    />
    <div class="content-panels">
      <PhotoBoothV2
        v-if="activePanel === ContentPickerSource.PhotoBooth"
        data-testid="contentPickerPhotoBooth"
        :is-lti-embedded="props.isLtiEmbedded"
        @open-in-new-window="emit('open-in-new-window')"
        @upload-file="(file) => startFileUpload(file, { contentSource: ContentPickerSource.PhotoBooth })"
        @close="closePanel"
      />
      <SurfaceVideoRecorder
        v-else-if="activePanel === ContentPickerSource.VideoRecorder && !props.useAgnosticContentPanels"
        data-testid="surfaceVideoRecorderContentPicker"
        :video-length-limit="videoLengthLimit"
        @select="(url) => selectLink(url, { contentSource: ContentPickerSource.VideoRecorder })"
        @upload-file="
          (file, options) => startFileUpload(file, { contentSource: ContentPickerSource.VideoRecorder, ...options })
        "
        @close="closePanel"
      />
      <VideoRecorder
        v-else-if="activePanel === ContentPickerSource.VideoRecorder && props.useAgnosticContentPanels"
        data-testid="surfaceVideoRecorderContentPicker"
        :video-length-limit="videoLengthLimit"
        @select="(url) => selectLink(url, { contentSource: ContentPickerSource.VideoRecorder })"
        @upload-file="
          (file, options) => startFileUpload(file, { contentSource: ContentPickerSource.VideoRecorder, ...options })
        "
        @close="closePanel"
      />
      <AudioRecorderV2
        v-else-if="activePanel === ContentPickerSource.AudioRecorder"
        :audio-length-limit="audioLengthLimit"
        data-testid="contentPickerAudioRecorder"
        :is-lti-embedded="props.isLtiEmbedded"
        @open-in-new-window="emit('open-in-new-window')"
        @select="(url) => selectLink(url, { contentSource: ContentPickerSource.AudioRecorder })"
        @close="closePanel"
      />
      <DrawingCanvas
        v-else-if="activePanel === ContentPickerSource.DrawingCanvas"
        @close="closePanel"
        @upload-file="(file) => startFileUpload(file, { contentSource: ContentPickerSource.DrawingCanvas })"
      />

      <SurfacePadletPickerV2
        v-else-if="
          isUserRegistered && activePanel === ContentPickerSource.PadletBrowser && !props.useAgnosticContentPanels
        "
        :user="user"
        :post-cid="activeDraft ? activeDraft.cid : undefined"
        :padlet-selection-intent="PadletSelectionIntent.Attachment"
        @close="closePanel"
        @select="selectWall"
      />
      <PadletPicker
        v-else-if="
          isUserRegistered && activePanel === ContentPickerSource.PadletBrowser && props.useAgnosticContentPanels
        "
        :user="user"
        :post-cid="undefined"
        :padlet-selection-intent="PadletSelectionIntent.Attachment"
        :is-closeable="true"
        :is-center-modal="true"
        @close="closePanel"
        @select="selectWall"
      />
      <SurfaceLocationPickerV2
        v-else-if="
          activePanel === ContentPickerSource.MapBrowser &&
          isAppUsing('locationPickerV2') &&
          !props.useAgnosticContentPanels
        "
        @select="(url) => selectLink(url, { contentSource: ContentPickerSource.MapBrowser })"
        @close="closePanel"
      />
      <LocationPicker
        v-else-if="
          activePanel === ContentPickerSource.MapBrowser &&
          isAppUsing('locationPickerV2') &&
          props.useAgnosticContentPanels
        "
        @close="closePanel"
        @select="(url) => selectLink(url, { contentSource: ContentPickerSource.MapBrowser })"
      />
      <MapBrowser
        v-else-if="activePanel === ContentPickerSource.MapBrowser"
        @close="closePanel"
        @select="(url) => selectLink(url, { contentSource: ContentPickerSource.MapBrowser })"
      />
      <ScreenshareRecorder
        v-else-if="!isAppUsing('screenRecorderV2') && activePanel === ContentPickerSource.ScreenshareRecorder"
        :video-length-limit="videoLengthLimit"
        @close="closePanel"
        @select="(url) => selectLink(url, { contentSource: ContentPickerSource.ScreenshareRecorder })"
        @caption="setCaption"
      />
      <SurfacePollBuilder
        v-else-if="activePanel === ContentPickerSource.Poll"
        :existing-poll="pollDraft"
        @close="closePanel"
        @create-poll="createPoll"
        @edit-poll="editPoll"
      />
      <GoogleDrivePicker
        v-else-if="activePanel === ContentPickerSource.GoogleDrive"
        :max-file-size="maxFileUploadSize"
        :upload-key="googleDriveUploadKey"
        :accepted-file-types="googleDrivePickerFileTypes"
        :google-drive-api-key="googleDriveApiKey"
        :google-app-id="googleAppId"
        @start="$emit('upload-start', { url: $event })"
        @error="$emit('upload-error')"
        @progress="$emit('upload-progress', $event)"
        @success="(url) => selectLink(url, { contentSource: ContentPickerSource.GoogleDrive })"
        @close="closePanel"
      />
      <AiTalk v-else-if="activePanel === ContentPickerSource.AiTalk" @close="closePanel" @success="saveAiTalkAudio" />
      <StickerPickerModal
        v-else-if="activePanel === ContentPickerSource.Stickers"
        @close="closePanel"
        @pick="(url) => selectLink(url, { contentSource: ContentPickerSource.Stickers })"
      />
    </div>
    <div class="absolute top-0 hidden start-0">
      <!-- eslint-disable vuejs-accessibility/form-control-has-label -->
      <input
        id="content-hidden-file-input"
        type="file"
        :accept="acceptOnlyImages ? 'image/*' : undefined"
        :multiple="multipleImagesSupported"
        hidden
        @change="(e) => selectFile(e, { contentSource: ContentPickerSource.FileBrowser })"
        @cancel="checkAndCloseContentPicker"
      />
      <input
        v-if="enableHiddenInputs"
        id="content-hidden-image-input"
        label="content-hidden-image-input"
        type="file"
        accept="image/*"
        :capture="hiddenInputCapture"
        :multiple="multipleImagesSupported"
        hidden
        @change="(e) => selectFile(e, { contentSource: ContentPickerSource.PhotoBooth })"
      />
      <input
        v-if="enableHiddenInputs"
        id="content-hidden-video-input"
        label="content-hidden-video-input"
        type="file"
        accept="video/*"
        :capture="hiddenInputCapture"
        hidden
        @change="(e) => selectFile(e, { contentSource: ContentPickerSource.VideoRecorder })"
      />
      <!-- currently only used by comment attachments; which only accept single attachment -->
      <input
        v-if="enableHiddenInputs"
        id="content-hidden-media-input"
        label="content-hidden-media-input"
        type="file"
        accept="image/*, video/*, audio/*"
        hidden
        @change="(e) => selectFile(e, { contentSource: ContentPickerSource.FileBrowser })"
      />
    </div>
  </div>
</template>
